import { RoleFragment } from '@gql'
import {
  Button,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
} from '@chakra-ui/react'
import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDownIcon } from 'src/icons'

// The role filter of the Checklist and Indicators steps.
//
// 🔑 The choice is written on the step, so it is shared by everyone in the
// meeting and survives a reload (REU04-D24) — the same place the Tasks step
// keeps its own filters. In a meeting everybody looks at one screen; a personal
// filter would make what each person sees drift from what is being discussed.

interface Props {
  roles: RoleFragment[]
  selectedIds: string[]
  editable: boolean
  onChange(rolesIds: string[]): void
}

export default function MeetingStepRoleFilter({
  roles,
  selectedIds,
  editable,
  onChange,
}: Props) {
  const { t } = useTranslation()

  const handleChange = useCallback(
    (value: string | string[]) => {
      onChange(typeof value === 'string' ? [value] : value)
    },
    [onChange]
  )

  // Filtering a list of one is a control that can only ever do harm.
  if (roles.length < 2) return null

  const label =
    selectedIds.length === 0
      ? t('MeetingStepRoleFilter.all')
      : t('MeetingStepRoleFilter.some', { count: selectedIds.length })

  return (
    <Menu closeOnSelect={false}>
      <MenuButton
        as={Button}
        size="sm"
        variant="outline"
        rightIcon={<ChevronDownIcon size={18} />}
        isDisabled={!editable}
        mb={4}
      >
        {label}
      </MenuButton>
      <MenuList zIndex={10}>
        <MenuOptionGroup
          type="checkbox"
          value={selectedIds}
          onChange={handleChange}
        >
          {roles.map((role) => (
            <MenuItemOption key={role.id} value={role.id}>
              {role.name}
            </MenuItemOption>
          ))}
        </MenuOptionGroup>
        <MenuDivider />
        {/* An action, not an option: it belongs outside the option group, and
            must not borrow its checkmark mechanics. */}
        <MenuItem onClick={() => onChange([])}>
          {t('MeetingStepRoleFilter.reset')}
        </MenuItem>
      </MenuList>
    </Menu>
  )
}
